import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CommonIcons from "../../../assets/icons";
import { LocalStorageKey } from "../../../constants/localStorageKeys";
import { MapContext } from "../MapAdapter/core/MapContext";
import { AOI_FEATURE_KEY } from "../MapAdapter/plugins/aoi/consts";
import { addAOIFeature, removeAOIFeature, setDrawingAOIMode } from "../MapAdapter/store/slices/aoiSlice";
import { UniversalMap } from "../MapAdapter/UniversalMap";
import AssetsComp from "./AssetsImage";
import Thumbnail from "./ThumbnailToken";
import { flyToGeometry, formatKey, formatValue, getThumbnail } from "./ultis";

const isArrayOfObjects = (val: any) =>
{
   return Array.isArray(val) && val.length > 0 && typeof val[0] === "object";
};

// Turn any value into a readable string (avoids "[object Object]").
const labelOfObject = (item: any): string =>
   item?.name ??
   item?.common_name ??
   item?.title ??
   item?.id ??
   Object.entries(item ?? {})
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ");

const formatGroupValue = (value: any): string =>
{
   if (Array.isArray(value))
   {
      if (value.length === 0) return "-";
      return value
         .map((item) =>
            item !== null && typeof item === "object"
               ? labelOfObject(item)
               : String(item),
         )
         .join(", ");
   }
   if (value !== null && typeof value === "object")
   {
      return labelOfObject(value);
   }
   return value === null || value === undefined ? "-" : String(value);
};

export default function ImageInfo({ data, open }: any)
{
   const dispatch = useDispatch()
   const staticConfigs = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "",
   );
   const baseUrl = staticConfigs?.REACT_APP_API_BASE_URL;

   const { t } = useTranslation();

   const groupedResources = React.useMemo(() =>
   {
      const props = data?.properties || {};

      const grouped: any = {};

      Object.entries(props).forEach(([key, value]: any) =>
      {
         // case 1: key có prefix (view:xxx)
         if (key.includes(":"))
         {
            const [prefix, name] = key.split(":");

            if (!grouped[prefix]) grouped[prefix] = [];

            grouped[prefix].push({
               key,
               name,
               value: formatGroupValue(value),
            });
         }

         // case 2: array of objects → auto group
         else if (isArrayOfObjects(value))
         {
            grouped[key] = value?.map((item: any, index: number) => ({
               key: `${key}_${index}`,
               name: item.name || `item ${index + 1}`,
               value: Object.entries(item)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(", "),
            }));
         }
      });

      return grouped;
   }, [data]);

   const ungroupedResources = React.useMemo(() =>
   {
      const props = data?.properties || {};

      return Object.entries(props).filter(
         ([key, value]) =>
            !key.includes(":") && !isArrayOfObjects(value),
      );
   }, [data]);

   const [type, setType] = useState('Information')
   const [engine, setEngine] = useState<any>(null);

   useEffect(() =>
   {
      if (!data?.geometry || !engine?.map || !open) return;

      const featureId = `${AOI_FEATURE_KEY}-${data?.id}`;

      flyToGeometry(data?.geometry, engine?.map);
      dispatch(setDrawingAOIMode('VIEW'))
      dispatch(
         addAOIFeature({
            id: featureId,
            geometry: data?.geometry,
         }),
      );

      setTimeout(() =>
      {
         dispatch(setDrawingAOIMode('NONE'))
      }, 1500);

      // cleanup khi đóng hoặc unmount
      return () =>
      {
         dispatch(removeAOIFeature(featureId));
      };
   }, [data?.id, engine, open]);




   return (
      <MapContext.Provider
         value={{
            engine: engine,
            pluginManager: null,
            mapType: "maplibre",
            mapInstance: engine?.map,
         }}
      >
         <div className="w-full  text-white rounded-lg p-4">
            {/* Header */}

            <div className="grid grid-cols-2 gap-4 h-[600px]">
               {/* LEFT PANEL */}
               <div className="bg-[var(--neutral-900)] rounded-lg p-4 h-full overflow-auto">
                  {/* Tabs */}
                  <div className="flex border-b border-[#3e4f6c] mb-4">
                     <button
                        onClick={() => setType("Information")}
                        className={`text-sm pb-2 mr-6 border-b-2 ${type === "Information"
                           ? "border-[#4CC2FF] text-[#4CC2FF]"
                           : "border-transparent text-gray-400"
                           }`}
                     >
                        {t("Information")}
                     </button>

                     <button
                        onClick={() => setType("Assets")}
                        className={`text-sm pb-2 border-b-2 ${type === "Assets"
                           ? "border-[#4CC2FF] text-[#4CC2FF]"
                           : "border-transparent text-gray-400"
                           }`}
                     >
                        {t("Assets")}
                     </button>

                  </div>

                  {/* Info */}

                  {
                     type === 'Information' && (
                        <>
                           <div className="space-y-2 text-sm">
                              {
                                 ungroupedResources.length > 0 && (



                                    <div className="text-xs text-gray-400">
                                       {ungroupedResources.map(([key, value]: any) => (
                                          <div key={key} className="flex justify-between mb-2 w-full" style={{ fontSize: '14px' }}>
                                             <span className="w-1/3 ">{t(formatKey(key))}</span>
                                             <span className="text-white break-all w-2/3 flex justify-end">
                                                {formatValue(value)}
                                             </span>
                                          </div>
                                       ))}
                                    </div>

                                 )
                              }
                           </div>


                           <div className="mt-4 space-y-2">
                              {Object.entries(groupedResources).map(([group, items]: any) => (
                                 <details
                                    key={group}
                                    className="bg-[var(--neutral-800)] rounded-md px-3 py-2 cursor-pointer group"
                                 >
                                    <summary className="flex items-center justify-between text-sm capitalize list-none">
                                       {group}

                                       <span className="transition-transform group-open:rotate-180">
                                          <CommonIcons.ChevronUpIcon />
                                       </span>
                                    </summary>

                                    <div className="text-xs text-gray-400 mt-2 space-y-1">
                                       {items.map((item: any) => (
                                          <div key={item.key} className="flex justify-between">
                                             <span>{item.name}</span>
                                             <span className="text-gray-300 break-all">
                                                {String(item.value ?? "-")}
                                             </span>
                                          </div>
                                       ))}
                                    </div>
                                 </details>
                              ))}
                           </div>
                        </>
                     )
                  }
                  {
                     type === "Assets" && <AssetsComp data={data?.assets} />
                  }



               </div>

               {/* RIGHT PANEL */}
               <div className="space-y-4 h-full">
                  {/* Preview */}
                  <div className="bg-[var(--neutral-900)] rounded-lg p-4 h-[300px]">
                     <p className="text-xs text-gray-400 mb-2">{t("PREVIEW")}</p>

                     <div className="bg-black rounded-md h-[250px] flex items-center justify-center">
                        <Thumbnail
                           url={getThumbnail(data?.assets, baseUrl)}
                           token={localStorage.getItem("token") ?? ""}
                        />
                     </div>
                  </div>

                  {/* Footprints */}
                  <div className="bg-[var(--neutral-900)] rounded-lg p-4 h-[300px]">
                     <p className="text-xs text-gray-400 mb-2">{t("FOOTPRINTS")}</p>

                     <div className="bg-black rounded-md h-[250px] flex items-center justify-center">
                        <UniversalMap
                           mapType="maplibre"
                           showEngine={false}
                           showDraw={false}
                           showDrawAOI={false}
                           showMeasure={false}
                           view={{
                              center: [108.23653393802581, 16.1039225743402],
                              zoom: 5,
                           }}
                           showBaseMap={false}
                           showMapControl={false}
                           onMapReady={setEngine}
                        />

                     </div>
                  </div>
               </div>
            </div>
         </div>
      </MapContext.Provider>

   );
}
